/* ==========================================================================
   Shopify 2.0 Theme JavaScript
   Vanilla JS · Mobile-First · RTL
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     1. Utility Helpers
     ======================================================================== */

  /**
   * Debounce function calls
   */
  function debounce(fn, delay) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  /**
   * Fetch wrapper for Shopify AJAX API
   */
  function shopifyFetch(url, options) {
    var defaults = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
    var config = Object.assign({}, defaults, options);
    return fetch(url, config).then(function (res) {
      if (!res.ok) throw new Error('Network response was not ok: ' + res.status);
      return res.json();
    });
  }

  /**
   * Customer-visible strings come from the locale file via the layout
   * (window.__themeStrings, keys under general.js.*).
   */
  function t(key) {
    var strings = window.__themeStrings || {};
    return strings[key] || '';
  }

  /**
   * Price with the store's currency symbol (window.__themeConfig.currencySymbol).
   * The subunit divisor comes from the store's own currency — never assume 100,
   * or every 3-decimal currency (KWD, BHD, OMR) shows ten times its real price.
   */
  function formatMoney(subunits) {
    var decimals = typeof window.themeMoneyDecimals === 'number' ? window.themeMoneyDecimals : 2;
    var divisor = window.themeMoneyDivisor || Math.pow(10, decimals);
    var amount = (Number(subunits) / divisor).toFixed(decimals);
    var symbol = (window.__themeConfig && window.__themeConfig.currencySymbol) || '';
    return symbol ? amount + ' ' + symbol : amount;
  }

  /* ========================================================================
     2. Sticky Header
     ======================================================================== */
  function initStickyHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;

    var scrollThreshold = 50;

    function onScroll() {
      if (window.scrollY > scrollThreshold) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    }

    window.addEventListener('scroll', debounce(onScroll, 10), { passive: true });
    onScroll();
  }

  /* ========================================================================
     3. Mobile Menu Toggle
     ======================================================================== */
  function initMobileMenu() {
    var hamburger = document.querySelector('[data-hamburger]');
    var drawer = document.querySelector('[data-mobile-drawer]');
    var overlay = document.querySelector('[data-drawer-overlay]');
    var closeBtn = document.querySelector('[data-drawer-close]');

    if (!hamburger || !drawer) return;

    function openDrawer() {
      hamburger.classList.add('is-active');
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      document.body.classList.add('drawer-open');
      hamburger.setAttribute('aria-expanded', 'true');

      // Trap focus — focus first link
      var firstLink = drawer.querySelector('a, button');
      if (firstLink) firstLink.focus();
    }

    function closeDrawer() {
      hamburger.classList.remove('is-active');
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      hamburger.setAttribute('aria-expanded', 'false');
      hamburger.focus();
    }

    hamburger.addEventListener('click', function () {
      var isOpen = drawer.classList.contains('is-open');
      isOpen ? closeDrawer() : openDrawer();
    });

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });
  }

  /* ========================================================================
     4. Cart AJAX Functionality
     ======================================================================== */
  function initCart() {
    var cartCountEls = document.querySelectorAll('[data-cart-count]');
    var cartDrawer = document.querySelector('[data-cart-drawer]');
    var cartDrawerItems = document.querySelector('[data-cart-drawer-items]');
    var cartDrawerTotal = document.querySelector('[data-cart-drawer-total]');
    var cartOverlay = document.querySelector('[data-cart-overlay]');
    var cartCloseBtn = document.querySelector('[data-cart-close]');

    // Open / close cart drawer
    function openCartDrawer() {
      if (!cartDrawer) return;
      cartDrawer.classList.add('is-open');
      if (cartOverlay) cartOverlay.classList.add('is-open');
      document.body.classList.add('drawer-open');
    }

    function closeCartDrawer() {
      if (!cartDrawer) return;
      cartDrawer.classList.remove('is-open');
      if (cartOverlay) cartOverlay.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
    }

    // Cart drawer open trigger
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-cart-toggle]');
      if (trigger) {
        e.preventDefault();
        openCartDrawer();
        refreshCartDrawer();
      }
    });

    if (cartCloseBtn) cartCloseBtn.addEventListener('click', closeCartDrawer);
    if (cartOverlay) cartOverlay.addEventListener('click', closeCartDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && cartDrawer && cartDrawer.classList.contains('is-open')) {
        closeCartDrawer();
      }
    });

    /**
     * Update cart count badges
     */
    function updateCartCount(count) {
      cartCountEls.forEach(function (el) {
        el.textContent = count > 0 ? count : '';
      });
    }

    /**
     * Show mini cart notification
     */
    function showCartNotification(item) {
      var notification = document.querySelector('[data-cart-notification]');
      if (!notification) return;

      var img = notification.querySelector('img');
      var text = notification.querySelector('[data-cart-notification-text]');

      if (img && item.image) img.src = item.image;
      if (text) text.textContent = t('added_to_cart').replace('{title}', item.product_title);

      notification.classList.add('is-visible');

      setTimeout(function () {
        notification.classList.remove('is-visible');
      }, 3000);
    }

    /**
     * Refresh cart drawer contents
     */
    function refreshCartDrawer() {
      shopifyFetch('/cart.js', { method: 'GET' }).then(function (cart) {
        updateCartCount(cart.item_count);
        renderCartItems(cart);
      }).catch(function (err) {
        console.error('Error refreshing cart:', err);
      });
    }

    /**
     * Render cart items inside drawer
     */
    function renderCartItems(cart) {
      if (!cartDrawerItems) return;

      if (cart.items.length === 0) {
        cartDrawerItems.innerHTML =
          '<div class="empty-state">' +
            '<p>' + t('cart_empty') + '</p>' +
            '<a href="' + ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'collections/all" class="btn btn-primary mt-lg">' + t('shop_now') + '</a>' +
          '</div>';
        if (cartDrawerTotal) cartDrawerTotal.textContent = formatMoney(0);
        return;
      }

      var html = '';
      cart.items.forEach(function (item) {
        html +=
          '<div class="cart-item" data-line-key="' + item.key + '">' +
            '<div class="cart-item-image">' +
              '<img src="' + (item.image || '') + '" alt="' + item.product_title + '" loading="lazy">' +
            '</div>' +
            '<div class="cart-item-details">' +
              '<div class="cart-item-title">' + item.product_title + '</div>' +
              '<div class="cart-item-price">' + formatMoney(item.final_line_price) + '</div>' +
              '<div class="quantity-selector mt-sm">' +
                '<button class="quantity-btn" data-cart-qty-change data-key="' + item.key + '" data-qty="' + (item.quantity - 1) + '" aria-label="' + t('qty_decrease') + '">−</button>' +
                '<input class="quantity-input" type="number" value="' + item.quantity + '" min="0" readonly>' +
                '<button class="quantity-btn" data-cart-qty-change data-key="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="' + t('qty_increase') + '">+</button>' +
              '</div>' +
              '<button class="cart-item-remove mt-sm" data-cart-qty-change data-key="' + item.key + '" data-qty="0">' + t('remove') + '</button>' +
            '</div>' +
          '</div>';
      });

      cartDrawerItems.innerHTML = html;
      if (cartDrawerTotal) cartDrawerTotal.textContent = formatMoney(cart.total_price);
    }

    /**
     * Add to cart
     */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      e.preventDefault();

      var form = btn.closest('form');
      var variantId = btn.dataset.variantId;
      var quantity = 1;

      // Get variant from form if not on button
      if (!variantId && form) {
        var variantInput = form.querySelector('[name="id"]');
        if (variantInput) variantId = variantInput.value;

        var qtyInput = form.querySelector('[name="quantity"]');
        if (qtyInput) quantity = parseInt(qtyInput.value, 10) || 1;
      }

      if (!variantId) return;

      btn.classList.add('btn-loading');
      btn.disabled = true;

      shopifyFetch('/cart/add.js', {
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: quantity })
      }).then(function (item) {
        showCartNotification(item);
        return shopifyFetch('/cart.js', { method: 'GET' });
      }).then(function (cart) {
        updateCartCount(cart.item_count);
      }).catch(function (err) {
        console.error('Add to cart error:', err);
      }).finally(function () {
        btn.classList.remove('btn-loading');
        btn.disabled = false;
      });
    });

    /**
     * Change cart quantity (in drawer)
     */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cart-qty-change]');
      if (!btn) return;
      e.preventDefault();

      var key = btn.dataset.key;
      var qty = parseInt(btn.dataset.qty, 10);
      if (qty < 0) qty = 0;

      shopifyFetch('/cart/change.js', {
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (cart) {
        updateCartCount(cart.item_count);
        renderCartItems(cart);
      }).catch(function (err) {
        console.error('Cart change error:', err);
      });
    });

    // Expose for external use
    window.StoreCart = {
      open: openCartDrawer,
      close: closeCartDrawer,
      refresh: refreshCartDrawer
    };
  }

  /* ========================================================================
     5. Quantity Selector (Product Page)
     ======================================================================== */
  function initQuantitySelectors() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-btn]');
      if (!btn) return;

      var wrapper = btn.closest('.quantity-selector');
      if (!wrapper) return;

      var input = wrapper.querySelector('.quantity-input');
      if (!input) return;

      var current = parseInt(input.value, 10) || 1;
      var min = parseInt(input.min, 10) || 1;
      var max = parseInt(input.max, 10) || 999;
      var action = btn.dataset.qtyBtn; // 'plus' or 'minus'

      if (action === 'plus' && current < max) {
        input.value = current + 1;
      } else if (action === 'minus' && current > min) {
        input.value = current - 1;
      }

      // Dispatch change event
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ========================================================================
     6. Before / After Slider
     ======================================================================== */
  function initBASliders() {
    var sliders = document.querySelectorAll('[data-ba-slider]');

    sliders.forEach(function (slider) {
      var afterEl = slider.querySelector('.ba-after');
      var handle = slider.querySelector('.ba-handle');
      if (!afterEl || !handle) return;

      var isDragging = false;

      function getPosition(e) {
        var rect = slider.getBoundingClientRect();
        var clientX = e.touches ? e.touches[0].clientX : e.clientX;
        var x = clientX - rect.left;
        var percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
        return percent;
      }

      function updatePosition(percent) {
        // In RTL, we keep percentage from left since CSS handles direction
        afterEl.style.width = percent + '%';
        handle.style.left = percent + '%';
      }

      function onStart(e) {
        isDragging = true;
        e.preventDefault();
        var percent = getPosition(e);
        updatePosition(percent);
      }

      function onMove(e) {
        if (!isDragging) return;
        e.preventDefault();
        var percent = getPosition(e);
        updatePosition(percent);
      }

      function onEnd() {
        isDragging = false;
      }

      // Mouse events
      slider.addEventListener('mousedown', onStart);
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onEnd);

      // Touch events
      slider.addEventListener('touchstart', onStart, { passive: false });
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', onEnd);
    });
  }

  /* ========================================================================
     7. Testimonials Auto-Carousel
     ======================================================================== */
  function initTestimonialsCarousel() {
    var carousels = document.querySelectorAll('[data-testimonials-carousel]');

    carousels.forEach(function (carousel) {
      var track = carousel.querySelector('.testimonials-track');
      var slides = carousel.querySelectorAll('.testimonial-slide');
      var dotsContainer = carousel.querySelector('.carousel-dots');

      if (!track || slides.length === 0) return;

      var currentIndex = 0;
      var autoplayInterval = null;
      var autoplayDelay = parseInt(carousel.dataset.autoplay, 10) || 5000;

      // Calculate visible slides
      function getVisibleCount() {
        if (window.innerWidth >= 1024) return 3;
        if (window.innerWidth >= 768) return 2;
        return 1;
      }

      function getMaxIndex() {
        return Math.max(0, slides.length - getVisibleCount());
      }

      // Create dots
      function createDots() {
        if (!dotsContainer) return;
        dotsContainer.innerHTML = '';
        var count = getMaxIndex() + 1;
        for (var i = 0; i < count; i++) {
          var dot = document.createElement('button');
          dot.className = 'carousel-dot' + (i === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', t('slide_label').replace('{n}', i + 1));
          dot.dataset.index = i;
          dotsContainer.appendChild(dot);
        }
      }

      function updateDots() {
        if (!dotsContainer) return;
        var dots = dotsContainer.querySelectorAll('.carousel-dot');
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === currentIndex);
        });
      }

      function goToSlide(index) {
        var maxIndex = getMaxIndex();
        currentIndex = Math.max(0, Math.min(index, maxIndex));

        var slideWidth = 100 / getVisibleCount();
        var offset = currentIndex * slideWidth;
        // RTL: translate positive to move right-to-left
        track.style.transform = 'translateX(' + offset + '%)';

        updateDots();
      }

      function next() {
        if (currentIndex >= getMaxIndex()) {
          goToSlide(0);
        } else {
          goToSlide(currentIndex + 1);
        }
      }

      function startAutoplay() {
        stopAutoplay();
        autoplayInterval = setInterval(next, autoplayDelay);
      }

      function stopAutoplay() {
        if (autoplayInterval) clearInterval(autoplayInterval);
      }

      // Dot click
      if (dotsContainer) {
        dotsContainer.addEventListener('click', function (e) {
          var dot = e.target.closest('.carousel-dot');
          if (!dot) return;
          goToSlide(parseInt(dot.dataset.index, 10));
          stopAutoplay();
          startAutoplay();
        });
      }

      // Pause on hover
      carousel.addEventListener('mouseenter', stopAutoplay);
      carousel.addEventListener('mouseleave', startAutoplay);

      // Touch swipe
      var touchStartX = 0;
      var touchEndX = 0;

      carousel.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].screenX;
        stopAutoplay();
      }, { passive: true });

      carousel.addEventListener('touchend', function (e) {
        touchEndX = e.changedTouches[0].screenX;
        var diff = touchStartX - touchEndX;
        // RTL: swipe left = next, swipe right = prev (reversed)
        if (Math.abs(diff) > 50) {
          if (diff < 0) {
            next();
          } else {
            goToSlide(currentIndex > 0 ? currentIndex - 1 : getMaxIndex());
          }
        }
        startAutoplay();
      }, { passive: true });

      // Init
      createDots();
      goToSlide(0);
      startAutoplay();

      // Recalculate on resize
      window.addEventListener('resize', debounce(function () {
        createDots();
        goToSlide(Math.min(currentIndex, getMaxIndex()));
      }, 200));
    });
  }

  /* ========================================================================
     8. Smooth Scroll for Anchor Links
     ======================================================================== */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;

      var targetId = link.getAttribute('href');
      if (targetId === '#' || !targetId) return;

      var target = document.querySelector(targetId);
      if (!target) return;

      e.preventDefault();

      var headerHeight = 0;
      var header = document.querySelector('.site-header');
      if (header) headerHeight = header.offsetHeight;

      var top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;

      window.scrollTo({
        top: top,
        behavior: 'smooth'
      });

      // Close mobile drawer if open
      var drawer = document.querySelector('[data-mobile-drawer]');
      if (drawer && drawer.classList.contains('is-open')) {
        var hamburger = document.querySelector('[data-hamburger]');
        if (hamburger) hamburger.click();
      }
    });
  }

  /* ========================================================================
     9. Image Lazy Loading (IntersectionObserver)
     ======================================================================== */
  function initLazyLoad() {
    var lazyImages = document.querySelectorAll('[data-src]');
    if (lazyImages.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      // Fallback: load all immediately
      lazyImages.forEach(function (img) {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.removeAttribute('data-src');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var img = entry.target;
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.removeAttribute('data-src');
        img.classList.add('animate-fadeIn');
        observer.unobserve(img);
      });
    }, {
      rootMargin: '200px 0px',
      threshold: 0.01
    });

    lazyImages.forEach(function (img) {
      observer.observe(img);
    });
  }

  /* ========================================================================
     10. Product Image Gallery
     ======================================================================== */
  function initProductGallery() {
    var galleries = document.querySelectorAll('[data-product-gallery]');

    galleries.forEach(function (gallery) {
      var mainImage = gallery.querySelector('[data-gallery-main] img');
      var thumbs = gallery.querySelectorAll('.product-gallery-thumb');

      if (!mainImage || thumbs.length === 0) return;

      // Thumbnail click
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var newSrc = thumb.dataset.fullSrc || thumb.querySelector('img').src;
          mainImage.src = newSrc;

          // Update active state
          thumbs.forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });

      // Swipe on main image for mobile
      var touchStartX = 0;
      var touchEndX = 0;
      var currentThumbIndex = 0;

      var mainContainer = gallery.querySelector('[data-gallery-main]');
      if (!mainContainer) return;

      mainContainer.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      mainContainer.addEventListener('touchend', function (e) {
        touchEndX = e.changedTouches[0].screenX;
        var diff = touchStartX - touchEndX;

        if (Math.abs(diff) < 50) return;

        // RTL: swipe left (diff > 0) goes to next, swipe right goes to prev
        if (diff > 0 && currentThumbIndex < thumbs.length - 1) {
          currentThumbIndex++;
        } else if (diff < 0 && currentThumbIndex > 0) {
          currentThumbIndex--;
        }

        thumbs[currentThumbIndex].click();
      }, { passive: true });
    });
  }

  /* ========================================================================
     11. Announcement Bar Dismiss
     ======================================================================== */
  function initAnnouncementBar() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-announcement-dismiss]');
      if (!btn) return;

      var bar = btn.closest('.announcement-bar');
      if (!bar) return;

      bar.classList.add('is-dismissed');

      // Remember dismissal in sessionStorage
      try {
        sessionStorage.setItem('announcement-dismissed', '1');
      } catch (err) {
        // Silently fail if storage unavailable
      }
    });

    // Check if previously dismissed
    try {
      if (sessionStorage.getItem('announcement-dismissed') === '1') {
        var bar = document.querySelector('.announcement-bar');
        if (bar) bar.classList.add('is-dismissed');
      }
    } catch (err) {
      // Silently fail
    }
  }

  /* ========================================================================
     12. Scroll Animations (IntersectionObserver)
     ======================================================================== */
  function initScrollAnimations() {
    var animatedEls = document.querySelectorAll('[data-animate]');
    if (animatedEls.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      animatedEls.forEach(function (el) {
        el.style.opacity = '1';
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var el = entry.target;
        var animation = el.dataset.animate || 'fadeIn';
        el.classList.add('animate-' + animation);
        observer.unobserve(el);
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    animatedEls.forEach(function (el) {
      el.style.opacity = '0';
      observer.observe(el);
    });
  }

  /* ========================================================================
     13. Initialize Everything on DOM Ready
     ======================================================================== */
  function init() {
    initStickyHeader();
    initMobileMenu();
    initCart();
    initQuantitySelectors();
    initBASliders();
    initTestimonialsCarousel();
    initSmoothScroll();
    initLazyLoad();
    initProductGallery();
    initAnnouncementBar();
    initScrollAnimations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
